import {
  EmptyState,
  ErrorState,
  LoadingState,
  WithHalfdot,
  Wordmark,
} from "@elyxeer/ui";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  Navigate,
  useSearchParams,
} from "react-router-dom";

import { UpcomingEvent } from "~/components/events/UpcomingEvent";
import { DashboardHighlightCard } from "~/components/highlights/DashboardHighlightCard";
import { ContinueSection } from "~/components/news/ContinueSection";
import { NewsCard } from "~/components/news/NewsCard";
import { NotificationBell } from "~/components/notifications/NotificationBell";
import { RoutineCard } from "~/components/routine/RoutineCard";
import { Screen } from "~/components/ui/Screen";
import { SectionHeading } from "~/components/ui/SectionHeading";
import { INTL_LOCALE, useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import {
  fetchDashboardHighlight,
  fetchNews,
  fetchUpcomingEvent,
} from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { useToday } from "~/lib/use-today";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";
import type { NewsArticleSummary } from "~/types/news";

/**
 * How many articles "Co je nového" carries.
 *
 * Seven, and no pagination: this section is the teaser, not the archive. Everything
 * older — and the topic filter for finding it — lives on Protocols, which the link
 * under the section points at. Seven is the lead plus a grid that stays full at every
 * width: six fills three columns two tall on a wide screen and two columns three tall
 * on a narrow desktop, so no row is ever left with a gap in it.
 */
const LATEST_COUNT = 7;

/**
 * The space between dashboard cards, in theme spacing units.
 *
 * One value for the gap under the lead and the gaps inside the grid, so the lead reads
 * as the first row of the same block rather than something floating above it.
 */
const CARD_GAP = { xs: 2, md: 3 } as const;

/** Keep time-sensitive dashboard cards current while the app is in the foreground. */
const DASHBOARD_REFRESH_INTERVAL_MS = 60_000;

/**
 * The search param that used to hold the calendar open over the dashboard.
 *
 * The calendar is a tab now, but `/news?calendar=1` was a shareable link while it was
 * an overlay, so it still has to land somewhere sensible.
 */
const LEGACY_CALENDAR_PARAM = "calendar";

/**
 * The air above a major section heading, in pixels.
 *
 * Sections are what the member scans on the way down, so the space between them is
 * bigger than any space inside one. Kept here rather than as an `sx` breakpoint object
 * because it is the same at every width — the separation is structural, not a matter of
 * how much room the viewport has.
 */
const SECTION_GAP = 48;

/**
 * The page width at which the routine leaves the top of the column for a sidebar.
 *
 * The routine card is built at a phone's width — two tiles and a seven-day strip — so
 * on a wide screen it gets a phone-width column of its own (`ASIDE_WIDTH`) and the
 * feed keeps the rest. Below this the two would leave the feed too narrow for its lead
 * to sit beside its cover, so the routine goes back on top, spanning the page.
 *
 * Measured on the page rather than the viewport, for the same reason as the events
 * calendar: the rail takes its width out of the viewport first.
 */
const WITH_ASIDE = "@1040";
const ASIDE_WIDTH = 360;

/**
 * How many columns the news grid gets, by page width.
 *
 * Three once the feed has the page to itself and room for them; two when the routine
 * sidebar takes its share, because three beside it would squeeze the titles. Six cards
 * fill either shape without a gap. A phone keeps one.
 */
const GRID_COLUMNS = {
  "@": "minmax(0, 1fr)",
  "@560": "repeat(2, minmax(0, 1fr))",
  "@900": "repeat(3, minmax(0, 1fr))",
  [WITH_ASIDE]: "repeat(2, minmax(0, 1fr))",
} as const;

/** Where the member's day sits: before noon, the afternoon, the evening and night. */
function greetingKey(hour: number): string {
  if (hour >= 4 && hour < 12) return "feed.greeting.morning";
  if (hour >= 12 && hour < 18) return "feed.greeting.afternoon";
  return "feed.greeting.evening";
}

/**
 * The dashboard — the app's home screen: the member's routine, upcoming event,
 * highlight, then news.
 *
 * Phone: one column, newest first, with the lockup at the top of the page. This is the
 * only screen that carries the lockup on a phone; the others are reached from the tab
 * bar and do not need re-branding. On desktop the side rail carries it instead, and
 * the page opens with a title like every other tab: today's date over a greeting that
 * follows the member's morning and evening, the two moments the routine is about.
 *
 * Desktop: the newest article becomes a wide hero and the rest fall into a grid, so a
 * 1440px window shows a feed rather than a phone screenshot stretched down the middle.
 * On a wide screen the routine moves into a sticky sidebar beside it (`WITH_ASIDE`) —
 * still first in the document, so a screen reader and the keyboard meet it first, as
 * they do on the phone.
 *
 * The round buttons in the header are the two things worth reaching from anywhere in
 * the feed: the notification bell, which is the only place the app says something is
 * waiting, and a shortcut into the Events tab. On a phone the header docks, which keeps
 * both one tap away however far the feed has scrolled; on desktop the rail already
 * keeps Events in reach, so the header scrolls away with the page title it now holds.
 */
/**
 * What's-new slots are measured as impression → click → consumption, so the card has
 * to report being seen as well as being opened. Written once here because the lead
 * and the grid emit the identical shape.
 */
function trackFeedCard(
  article: NewsArticleSummary,
  slot: "lead" | "grid",
  position: number,
): void {
  track(AnalyticsEventName.FeedImpression, {
    subjectType: "news",
    subjectId: article.id,
    subjectKey: article.slug,
    contentFormat: article.format,
    props: { slot, position },
  });
}

function trackFeedClick(
  article: NewsArticleSummary,
  slot: "lead" | "grid",
  position: number,
): void {
  track(AnalyticsEventName.FeedClick, {
    subjectType: "news",
    subjectId: article.id,
    subjectKey: article.slug,
    contentFormat: article.format,
    props: { slot, position },
  });
}

export function FeedRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const today = useToday();
  const [searchParams] = useSearchParams();
  const userId = useAuthStore((state) => state.user?.id ?? null);

  const legacyCalendarLink = searchParams.has(LEGACY_CALENDAR_PARAM);

  // Optional dashboard sections fail quietly. A failed card must not make the useful
  // news below it look as though the whole dashboard is unavailable.
  const { data: upcomingEvent } = useQuery({
    queryKey: queryKeys.upcomingEvent(locale),
    queryFn: ({ signal }) => fetchUpcomingEvent(locale, signal),
    refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  const { data: dashboardHighlight } = useQuery({
    queryKey: queryKeys.dashboardHighlight(locale),
    queryFn: ({ signal }) => fetchDashboardHighlight(locale, signal),
    refetchInterval: DASHBOARD_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.news(locale),
    queryFn: ({ signal }) =>
      fetchNews({ locale, limit: LATEST_COUNT, offset: 0, signal }),
  });

  const articles = data?.items ?? [];
  const [lead, ...rest] = articles;

  // Read at render, not kept on a timer. The dashboard mounts afresh whenever the
  // member comes back to it, and a greeting left an hour stale in a tab nobody is
  // looking at costs nothing — unlike `today`, it drives no data.
  const hour = new Date().getHours();
  const dateLabel = new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(today);

  if (legacyCalendarLink) {
    return <Navigate to="/events" replace />;
  }

  return (
    <>
      {/*
        On a phone, docked to the top so the calendar is always one tap away, however
        far down the feed the member has scrolled. On desktop it is the page's title
        row instead — the rail carries the lockup — and it scrolls with the page.

        The phone backdrop is a fade rather than a solid block: opaque behind the lockup
        and the button, then falling to transparent, so what scrolls underneath dissolves
        instead of being cut off against a hard edge. Gradients interpolate in
        premultiplied alpha, so `transparent` fades cleanly out of white rather than
        greying on the way.
      */}
      <Box
        component="header"
        sx={(theme) => {
          // The CSS variable, not the resolved colour: the header has to follow the
          // colour scheme, and a resolved value would freeze it to whichever scheme was
          // active when the theme was built.
          const paper =
            theme.vars?.palette.background.paper ??
            theme.palette.background.paper;

          return {
            position: { xs: "sticky", md: "static" },
            top: 0,
            // Above the cards, so they scroll under the fade rather than over it.
            zIndex: 2,
            background: {
              xs: `linear-gradient(to bottom, ${paper} 0%, ${paper} 82%, transparent 100%)`,
              md: "none",
            },
          };
        }}
      >
        {/*
          The same container as the page below, so on desktop the buttons end where the
          content column ends rather than at the far edge of the window.
        */}
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            paddingInline: { xs: 2.5, md: 5 },
            paddingTop: { xs: "calc(12px + env(safe-area-inset-top))", md: 6 },
            // Roomier than it looks on a phone: the bottom fifth is the fade.
            paddingBottom: { xs: 3, md: 0 },
          }}
        >
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Wordmark size={24} />
          </Box>

          {/* `flex: 1` so the title owns the row's width and never wraps early. */}
          <Box
            sx={{ display: { xs: "none", md: "block" }, flex: 1, minWidth: 0 }}
          >
            <Typography
              variant="OV1"
              component="p"
              sx={{ color: "text.muted" }}
              data-testid="feed.date"
            >
              {dateLabel}
            </Typography>
            <Typography
              variant="H1"
              sx={{ color: "text.primary", marginTop: 0.5 }}
              data-testid="feed.greeting"
            >
              <WithHalfdot size={11}>{t(greetingKey(hour))}</WithHalfdot>
            </Typography>
          </Box>

          {/*
            The bell owns the auto margin, so both controls ride the right edge as one
            group. It stays outlined while the calendar stays filled: the calendar is
            what the header is for, the bell is what the app has to tell you.
          */}
          <NotificationBell />

          <IconButton
            component={RouterLink}
            to="/events"
            aria-label={t("events.openCalendar")}
            data-testid="events.open-calendar"
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              color: "primary.contrastText",
              backgroundColor: "primary.main",
              "&:hover": { backgroundColor: "primary.dark" },
            }}
          >
            <CalendarMonthOutlinedIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Container>
      </Box>

      {/*
        The title opens the page and the sections follow it, so the space between them
        is the space between sections.
      */}
      <Screen disableTopInset sx={{ paddingTop: { md: `${SECTION_GAP}px` } }}>
        {/* The container `WITH_ASIDE` and `GRID_COLUMNS` measure. */}
        <Box sx={{ containerType: "inline-size" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                "@": "minmax(0, 1fr)",
                [WITH_ASIDE]: `minmax(0, 1fr) ${ASIDE_WIDTH}px`,
              },
              alignItems: "start",
              rowGap: `${SECTION_GAP}px`,
              columnGap: 5,
            }}
          >
            {/*
              Always present: it is the member's own, not a server-controlled slot.
              First in the document at every width; on a wide screen the grid places it
              in the right-hand column, where it stays in view while the feed scrolls.
            */}
            <Box
              sx={{
                gridColumn: { [WITH_ASIDE]: 2 },
                gridRow: { [WITH_ASIDE]: 1 },
                position: { [WITH_ASIDE]: "sticky" },
                top: { [WITH_ASIDE]: 48 },
              }}
            >
              <RoutineCard />
            </Box>

            <Box
              sx={{
                gridColumn: { [WITH_ASIDE]: 1 },
                gridRow: { [WITH_ASIDE]: 1 },
                display: "flex",
                flexDirection: "column",
                gap: `${SECTION_GAP}px`,
                minWidth: 0,
              }}
            >
              {/*
                Each optional section only gets its heading when it has content. A title
                over an empty server-controlled slot would look like a loading failure.
              */}
              {upcomingEvent ? (
                <Box
                  data-testid="events.upcoming-section"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 2, md: 3 },
                  }}
                >
                  <SectionHeading>{t("events.upcomingHeading")}</SectionHeading>

                  <UpcomingEvent event={upcomingEvent} />
                </Box>
              ) : null}

              {/* Where the member stopped. Its own query, and nothing when empty. */}
              {userId ? (
                <ContinueSection userId={userId} locale={locale} />
              ) : null}

              {dashboardHighlight ? (
                <Box
                  data-testid="highlights.section"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 2, md: 3 },
                  }}
                >
                  <SectionHeading>{t("highlights.heading")}</SectionHeading>

                  <DashboardHighlightCard highlight={dashboardHighlight} />
                </Box>
              ) : null}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 2, md: 3 },
                }}
              >
                <SectionHeading>{t("feed.title")}</SectionHeading>

                {isPending ? <LoadingState /> : null}

                {isError ? (
                  <ErrorState
                    message={
                      error instanceof Error ? error.message : t("feed.error")
                    }
                    onRetry={() => void refetch()}
                  />
                ) : null}

                {!isPending && !isError && articles.length === 0 ? (
                  <EmptyState message={t("feed.empty")} />
                ) : null}

                {articles.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: CARD_GAP,
                    }}
                    data-testid="news.list"
                  >
                    <NewsCard
                      article={lead}
                      featured
                      onImpression={() => trackFeedCard(lead, "lead", 0)}
                      onSelect={() => trackFeedClick(lead, "lead", 0)}
                    />

                    {rest.length > 0 ? (
                      <Box
                        sx={{
                          display: "grid",
                          // A fixed count, not auto-fill: auto-fill opens as many
                          // tracks as fit, so a wide window left the six cards in a
                          // ragged row with an empty slot beside them.
                          gridTemplateColumns: GRID_COLUMNS,
                          gap: CARD_GAP,
                        }}
                      >
                        {rest.map((article, index) => (
                          <NewsCard
                            key={article.id}
                            article={article}
                            onImpression={() =>
                              trackFeedCard(article, "grid", index + 1)
                            }
                            onSelect={() =>
                              trackFeedClick(article, "grid", index + 1)
                            }
                          />
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                ) : null}

                {/*
                  The way out of the teaser. Rendered whenever the feed loaded at all —
                  including when these seven are everything there is — because it is
                  also how the member discovers that the library and its topic filter
                  exist.
                */}
                {!isPending && !isError ? (
                  <Button
                    component={RouterLink}
                    to="/protocols"
                    variant="outlined"
                    data-testid="news.see-all"
                    sx={{
                      alignSelf: { md: "center" },
                      minWidth: { md: 240 },
                      marginTop: { xs: 1, md: 2 },
                    }}
                  >
                    {t("feed.seeAll")}
                  </Button>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>
      </Screen>
    </>
  );
}
